import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RoofSide } from "building-types/settings/roofTypes";
import type {
  EaveType,
  EaveCutType,
  WrapFriezeBoardMode,
  RakeFriezeBoardMode,
} from "building-types/settings/settingsTypesEnum";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import { type PolyLine3 } from "geom/polyLine3";
import type { Vec3 } from "geom/vec3";

export enum RoofPerimeterRailType {
  RAKE = "RAKE",
  EAVE = "EAVE",
}

export enum RoofPerimeterRailCondition {
  NONE = "NONE",
  COPLANAR_WALL = "COPLANAR_WALL",
  NON_COPLANAR_RAKE = "NON_COPLANAR_RAKE",
}

export enum RoofTrimType {
  FASCIA_BOARD = "FASCIA_BOARD",
  SOFFIT = "SOFFIT",
  FRIEZE_BOARD = "FRIEZE_BOARD",
  FASCIA_TOP_TRIM = "FASCIA_TOP_TRIM",
}

export enum RoofRailCornerType {
  NONE = "NONE",
  COPLANAR = "COPLANAR",
  INSIDE = "INSIDE",
  OUTSIDE = "OUTSIDE",
}

export enum RoofRailEndCondition {
  SQUARED = "SQUARED",
  PLUMB = "PLUMB",
  EXTENDED = "EXTENDED", // this has the same effect as the null
}

export interface RoofPerimeterRail {
  readonly id: string;
  readonly type: RoofPerimeterRailType;
  readonly subRoofId: string;
  readonly edgeIndex: number;
  readonly isDormer: boolean;
  readonly side: RoofSide;
  readonly isStartEdge: boolean;
  readonly isEndEdge: boolean;
  readonly prevPerimeterRail?: RoofPerimeterRail;
  readonly nextPerimeterRail?: RoofPerimeterRail;
  readonly startCondition: RoofPerimeterRailCondition;
  readonly endCondition: RoofPerimeterRailCondition;
  readonly appliedStartExtension: number;
  readonly appliedEndExtension: number;
  readonly edgePlane: Plane;
  readonly baseEdge: PolyLine2;
  readonly wallLimitsRail: PolyLine2;
  readonly rail: PolyLine3;
  readonly baseRail: PolyLine3;
  readonly normals: Vec3[];
  readonly baseNormals: Vec3[];
  readonly rafterDirections: Vec3[];
  readonly baseRafterDirections: Vec3[];
  readonly eaveType: EaveType;
  readonly eaveCutType: EaveCutType;
  readonly eaveReturnDistance: number;
  readonly exteriorWallOffsetWithSheathing: number;
  readonly exteriorWallOffsetWithFinish: number;
  readonly exteriorWallOffsetFromOverhangDatum: number;
  readonly overhang: number;
  readonly roofEdgeStartOverhang: number;
  readonly roofEdgeEndOverhang: number;
  readonly includeExposedRafters?: boolean;
  readonly includeFasciaBoard?: boolean;
  readonly fasciaBoardSize: number;
  readonly fasciaMaterial?: ExteriorMaterial;
  readonly fasciaTopTrimMaterial?: ExteriorMaterial;
  readonly includeSoffit?: boolean;
  readonly soffitOffset: number;
  readonly soffitMaterial?: ExteriorMaterial;
  readonly friezeBoard: FriezeBoard[] | undefined;
  readonly friezeBoardMaterialLegacy?: ExteriorMaterial;
  readonly rakeFriezeBoardExtents?: RakeFriezeBoardMode;
  readonly wrapFriezeBoardModeAtStart: WrapFriezeBoardMode;
  readonly wrapFriezeBoardModeAtEnd: WrapFriezeBoardMode;
  readonly includeFasciaTopTrim?: boolean;
  readonly fasciaTopTrimSize: number;
  readonly fasciaBoardThickness: number;
  readonly adjustFasciaBoardSize: boolean;
  readonly eaveCutTypeAtStart: RoofRailEndCondition;
  readonly eaveCutTypeAtEnd: RoofRailEndCondition;
}

export type ConnectedRoofPerimeterRailFilter = (
  roofPerimeterRail: RoofPerimeterRail,
  otherRoofPerimeterRail: RoofPerimeterRail,
  isEnd: boolean,
  isOtherEnd: boolean,
) => boolean;

export interface RoofRailConerInfo {
  readonly type: RoofRailCornerType;
  readonly neighborRail?: PolyLine3;
  readonly miterPlane?: Plane;
  readonly otherPerimeterRail?: RoofPerimeterRail;
  readonly angle: number;
}

export interface RoofPerimeterWallRail {
  readonly rail: PolyLine3;
  readonly railWithCorners: PolyLine3;
  readonly startCornerType: RoofRailCornerType;
  readonly endCornerType: RoofRailCornerType;
  readonly prevRoofPerimeterRail: RoofPerimeterRail | undefined;
  readonly nextRoofPerimeterRail: RoofPerimeterRail | undefined;
}
